import React, { useEffect, useRef } from "react";
import {
    Box,
    Button,
    Divider,
    Grid,
    Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import styles from "./style.js";
import OutstandingStatusBanner from "../../utils/Alert/OutstandingStatusBanner";

const PlanPage = ({
    plans = [],
    isLoading = false,
    emptyText = "No plans available.",
    onSwitchPlan = () => {},
    switchingPlanId = "",
}) => {
    const currentPlanCardRef = useRef(null);

    useEffect(() => {
        if (!isLoading && currentPlanCardRef.current) {
            currentPlanCardRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [isLoading]);

    return (
        <Box sx={styles.pageWrap}>
            <Box sx={styles.pageContainer}>
                <Box sx={styles.headerWrap}>
                    <Typography sx={styles.pageTitle}>Plan Management</Typography>
                    <Typography sx={styles.pageSubTitle}>
                        Choose the right plan for your organization.
                    </Typography>
                </Box>

                <OutstandingStatusBanner />
 
                {!isLoading && plans.length ? (
                    <Grid container columnSpacing={2.5} rowSpacing={6}>
                        {plans.map((plan) => {
                            const isCurrent = plan.currentPlan === true;
                            const canSwitch = plan.canSwitch === true;
                            const isSwitching = switchingPlanId === plan.id;

                            return (
                            <Grid item xs={12} sm={6} md={6} lg={3} key={plan.id}>
                                <Box
                                    ref={isCurrent ? currentPlanCardRef : null}
                                    sx={{
                                        ...styles.planCard,
                                        ...(plan.popular ? styles.popularCard : {}),
                                        ...(isCurrent ? styles.currentCard : {}),
                                    }}
                                >
                                    <Box sx={styles.cardHeadRow}>
                                        <Typography sx={styles.planName}>{plan.name}</Typography>
                                        {isCurrent ? (
                                            <Box sx={styles.currentBadge}>
                                                <CheckCircleOutlineIcon sx={{ ...styles.popularIcon, color: styles.currentBadgeText.color }} />
                                                <Typography sx={styles.currentBadgeText}>Current Plan</Typography>
                                            </Box>
                                        ) : plan.popular ? (
                                            <Box sx={styles.popularBadge}>
                                                <AutoAwesomeIcon sx={styles.popularIcon} />
                                                <Typography sx={styles.popularText}>Popular</Typography>
                                            </Box>
                                        ) : null}
                                    </Box>
 
                                    <Typography sx={styles.planDescription}>
                                        {plan.description}
                                    </Typography>
 
                                    <Box sx={styles.priceBlock}>
                                        <Box sx={styles.priceRow}>
                                            <Typography sx={styles.priceSign}>{plan.priceSign || "$"}</Typography>
                                            <Typography sx={styles.priceValue}>{plan.price}</Typography>
                                        </Box>
                                        <Typography sx={styles.billingText}>{plan.billingText}</Typography>
                                    </Box>
 
                                    <Button
                                        disableElevation
                                        variant={plan.buttonType === "filled" ? "contained" : "outlined"}
                                        disabled={isCurrent || isSwitching || !canSwitch}
                                        onClick={() => {
                                            if (canSwitch) {
                                                onSwitchPlan(plan);
                                            }
                                        }}
                                        sx={{
                                            ...styles.actionButton,
                                            ...(plan.buttonType === "filled" ? styles.filledBtn : {}),
                                            ...(plan.buttonType === "outline" ? styles.outlineBtn : {}),
                                            ...(plan.buttonType === "current" ? styles.currentPlanBtn : {}),
                                        }}
                                    >
                                        {isSwitching ? "Please wait..." : plan.buttonText}
                                    </Button>
 
                                    <Typography sx={styles.trialText}>{plan.trialText}</Typography>
 
                                    <Divider sx={styles.divider} />
 
                                    <Typography sx={styles.featureHeading}>Features</Typography>
                                    <Typography sx={styles.featureInfo}>{plan.featuresInfo}</Typography>
 
                                    <Box sx={styles.featureList}>
                                        {plan.features.map((feature, index) => (
                                            <Box key={`${plan.id}-${index}`} sx={styles.featureRow}>
                                                <CheckCircleOutlineIcon sx={styles.featureIcon} />
                                                <Typography sx={styles.featureText}>{feature}</Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            </Grid>
                            );
                        })}
                    </Grid>
                ) : !isLoading ? (
                    <Typography sx={styles.stateText}>{emptyText}</Typography>
                ) : null}
            </Box>
        </Box>
    );
};
 
export default PlanPage;
